import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";

import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import {
  ArrowPathIcon,
  CheckIcon,
  ChevronUpDownIcon,
  HashtagIcon,
  MagnifyingGlassIcon,
  TagIcon,
} from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import { FieldValues, UseFormGetValues, UseFormRegister, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import useVehiclesMutations from "../../data/useVehiclesMutations";
import { IPartCategory } from "../../models/Part";
import classNames from "../../utils/classNames";
import { MakeCombobox } from "../parts/MakeCombobox";
import { ModelCombobox } from "../parts/ModelCombobox";
import { VehicleDetailsInput } from "../parts/VehicleDetailsInput";
import { YearCombobox } from "../parts/YearCombobox";
import PopOutBox from "../shared/PopOutBox";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import ComboBoxMultipleInput from "../shared/Inputs/ComboBoxMultipleInput";

export type VehicleDetailFilterKey = "yearFrom" | "yearTo" | "trim" | "bodyStyle" | "numberOfDoors" | "fuelType" | "engineSize" | "engineCode" | "transmissionType";

export interface IVehiclePartSearchFields extends FieldValues {
  make: string;
  model: string;
  trim: string;
  bodyStyle: string;
  numberOfDoors: string;
  fuelType: string;
  transmissionType: string;
  engineSize: string;
  engineCode: string;
  yearFrom: string;
  yearTo: string;
  partNumber: string;
  partCategory: IPartCategory[];
  vrm: string;
  tagNumber: string;
  smartSearch: string;
}

export const defaultVehicleDetailFilterEnabled: Record<VehicleDetailFilterKey, boolean> = {
  yearFrom: true,
  yearTo: true,
  trim: true,
  bodyStyle: true,
  numberOfDoors: true,
  fuelType: true,
  engineSize: true,
  engineCode: true,
  transmissionType: true,
};

const VEHICLE_DETAIL_FILTER_STORAGE_KEY = "wilma:vehiclePartSearch:detailFilters";
const VEHICLE_DETAIL_FILTER_DEBOUNCE_MS = 1000;
export const BODY_STYLE_OPTIONS = ["Hatchback", "Saloon", "Estate", "SUV", "Coupe", "Convertible", "MPV", "Van", "Pickup", "Crossover"];
export const DOORS_OPTIONS = ["2", "3", "4", "5"];
export const FUEL_OPTIONS = ["Petrol", "Diesel", "Electric", "Hybrid", "Plug-in Hybrid", "LPG"];
export const TRANSMISSION_OPTIONS = ["Manual", "Automatic", "Semi-Automatic", "CVT", "DSG"];
export const ENGINE_SIZE_OPTIONS = ["1.0", "1.2", "1.4", "1.5", "1.6", "1.8", "2.0", "2.2", "2.5", "3.0", "3.5", "4.0", "5.0"];

export const vehiclePartSearchDefaultValues = {
  make: "",
  model: "",
  trim: "",
  bodyStyle: "",
  numberOfDoors: "",
  fuelType: "",
  transmissionType: "",
  engineSize: "",
  engineCode: "",
  yearFrom: "",
  yearTo: "",
  partNumber: "",
  partCategory: [],
  vrm: "",
  tagNumber: "",
  smartSearch: "",
};

function readSavedVehicleDetailFilterEnabled() {
  try {
    const saved = localStorage.getItem(VEHICLE_DETAIL_FILTER_STORAGE_KEY);
    if (!saved) return defaultVehicleDetailFilterEnabled;

    const parsed = JSON.parse(saved) as Partial<Record<VehicleDetailFilterKey, unknown>>;
    return Object.keys(defaultVehicleDetailFilterEnabled).reduce((result, key) => {
      const filterKey = key as VehicleDetailFilterKey;
      result[filterKey] = typeof parsed[filterKey] === "boolean" ? parsed[filterKey] as boolean : defaultVehicleDetailFilterEnabled[filterKey];
      return result;
    }, { ...defaultVehicleDetailFilterEnabled });
  } catch {
    return defaultVehicleDetailFilterEnabled;
  }
}

function saveVehicleDetailFilterEnabled(filterEnabled: Record<VehicleDetailFilterKey, boolean>) {
  try {
    localStorage.setItem(VEHICLE_DETAIL_FILTER_STORAGE_KEY, JSON.stringify(filterEnabled));
  } catch {
    // Ignore storage failures; the filters still work for the current session.
  }
}

function setFormValue<TFormFields extends IVehiclePartSearchFields>(
  setValue: UseFormSetValue<TFormFields>,
  name: keyof IVehiclePartSearchFields,
  value: unknown
) {
  setValue(name as any, value as any);
}

export function buildVehiclePartSearchBody<TFormFields extends IVehiclePartSearchFields>(
  data: TFormFields,
  vehicleDetailFilterEnabled: Record<VehicleDetailFilterKey, boolean>
): Record<string, any> {
  const body: Record<string, any> = {
    make: data.make,
    model: data.model,
    trim: data.trim,
    yearFrom: data.yearFrom,
    yearTo: data.yearTo,
    bodyStyle: data.bodyStyle,
    numberOfDoors: data.numberOfDoors,
    fuelType: data.fuelType,
    transmissionType: data.transmissionType,
    engineSize: data.engineSize,
    engineCode: data.engineCode,
    partNumber: data.partNumber,
    tagNumber: data.tagNumber,
    categoryIds: data.partCategory?.map((category) => category.id),
  };

  Object.entries(vehicleDetailFilterEnabled).forEach(([key, enabled]) => {
    if (!enabled) body[key] = undefined;
  });

  return body;
}

export function FilterStaticCombobox({
  label,
  value,
  onChange,
  options,
  disabled = false,
  placeholder,
  filterEnabled = true,
  onFilterEnabledChange,
}: {
  label: string;
  value: string | undefined;
  onChange: (next: string) => void;
  options: string[];
  disabled?: boolean;
  placeholder?: string;
  filterEnabled?: boolean;
  onFilterEnabledChange?: (enabled: boolean) => void;
}) {
  const { t } = useTranslation("stock");
  const [searchTerm, setSearchTerm] = useState(value ?? "");

  useEffect(() => {
    setSearchTerm(value ?? "");
  }, [value]);

  const isInputDisabled = disabled || !filterEnabled;

  const filtered = useMemo(() => {
    if (!searchTerm) return options;
    const needle = searchTerm.toLowerCase();
    return options.filter((opt) => opt.toLowerCase().includes(needle));
  }, [options, searchTerm]);

  return (
    <div className="mt-1">
      <label className={classNames("flex items-center gap-1.5 text-sm font-medium text-black", onFilterEnabledChange ? "cursor-pointer" : "")}>
        {onFilterEnabledChange && (
          <input
            type="checkbox"
            checked={filterEnabled}
            onChange={(event) => onFilterEnabledChange(event.target.checked)}
            disabled={disabled}
            className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
          />
        )}
        <span>{label}</span>
      </label>
      <Combobox
        as="div"
        value={value ?? ""}
        onChange={(next: string) => {
          onChange(next ?? "");
          setSearchTerm(next ?? "");
        }}
      >
        <div className="relative mt-1">
          <ComboboxInput
            className="w-full rounded-md border-1 border-gray-400 py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 disabled:bg-gray-100"
            onChange={(event) => {
              setSearchTerm(event.target.value);
              onChange(event.target.value);
            }}
            displayValue={(v: string) => v || searchTerm}
            placeholder={placeholder}
            disabled={isInputDisabled}
            autoComplete="off"
          />
          <ComboboxButton disabled={isInputDisabled} className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50">
            <ChevronUpDownIcon className="size-5 text-gray-400" aria-hidden="true" />
          </ComboboxButton>

          {!isInputDisabled && (
            <ComboboxOptions className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base ring-1 ring-gray-400 ring-opacity-5 focus:outline-none sm:text-sm">
              {filtered.length > 0 ? (
                filtered.slice(0, 100).map((opt) => (
                  <ComboboxOption
                    key={opt}
                    value={opt}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? "bg-primary-600 text-white" : "text-gray-900"}`
                    }
                  >
                    {({ selected, active }) => (
                      <>
                        <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>{opt}</span>
                        {selected ? (
                          <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? "text-white" : "text-primary-600"}`}>
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </ComboboxOption>
                ))
              ) : (
                <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                  {t('Press enter to use "{{term}}".', { term: searchTerm })}
                </div>
              )}
            </ComboboxOptions>
          )}
        </div>
      </Combobox>
    </div>
  );
}

interface IVehiclePartSearchFiltersProps<TFormFields extends IVehiclePartSearchFields> {
  register: UseFormRegister<TFormFields>;
  setValue: UseFormSetValue<TFormFields>;
  getValues: UseFormGetValues<TFormFields>;
  watch: UseFormWatch<TFormFields>;
  categories: IPartCategory[];
  categoriesLoading: boolean;
  disabled: boolean;
  isSearching: boolean;
  setIsSearching: Dispatch<SetStateAction<boolean>>;
  isSmartMatch: boolean;
  setIsSmartMatch: Dispatch<SetStateAction<boolean>>;
  vehicleDetailFilterEnabled: Record<VehicleDetailFilterKey, boolean>;
  setVehicleDetailFilterEnabled: Dispatch<SetStateAction<Record<VehicleDetailFilterKey, boolean>>>;
  onApplySmartSearch: (body: Record<string, any>) => void;
  getAdditionalSmartSearchBody?: (data: TFormFields) => Record<string, any>;
}

export default function VehiclePartSearchFilters<TFormFields extends IVehiclePartSearchFields>({
  register,
  setValue,
  getValues,
  watch,
  categories,
  categoriesLoading,
  disabled,
  isSearching,
  setIsSearching,
  isSmartMatch,
  setIsSmartMatch,
  vehicleDetailFilterEnabled,
  setVehicleDetailFilterEnabled,
  onApplySmartSearch,
  getAdditionalSmartSearchBody,
}: IVehiclePartSearchFiltersProps<TFormFields>) {
  const { t } = useTranslation("stock");
  const { vrmLookupData } = useVehiclesMutations();
  const [fetchLoading, setFetchLoading] = useState(false);
  const [storageLoaded, setStorageLoaded] = useState(false);
  const [draftVehicleDetailFilterEnabled, setDraftVehicleDetailFilterEnabled] = useState<Record<VehicleDetailFilterKey, boolean>>(vehicleDetailFilterEnabled);
  const vehicleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const smartMatchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const filterEnabledTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const watchMake = watch("make" as any);
  const watchModel = watch("model" as any);
  const watchYearFrom = watch("yearFrom" as any);
  const watchYearTo = watch("yearTo" as any);
  const watchBodyStyle = watch("bodyStyle" as any);
  const watchNumberOfDoors = watch("numberOfDoors" as any);
  const watchFuelType = watch("fuelType" as any);
  const watchEngineSize = watch("engineSize" as any);
  const watchEngineCode = watch("engineCode" as any);
  const watchTransmission = watch("transmissionType" as any);

  const isLookupDisabled = disabled || fetchLoading;

  const setVehicleDetailFilterEnabledValue = (key: VehicleDetailFilterKey, enabled: boolean) => {
    setDraftVehicleDetailFilterEnabled((current) => ({ ...current, [key]: enabled }));
  };

  useEffect(() => {
    const savedFilterEnabled = readSavedVehicleDetailFilterEnabled();
    setDraftVehicleDetailFilterEnabled(savedFilterEnabled);
    setVehicleDetailFilterEnabled(savedFilterEnabled);
    setStorageLoaded(true);
  }, [setVehicleDetailFilterEnabled]);

  useEffect(() => {
    if (!storageLoaded) return;
    saveVehicleDetailFilterEnabled(draftVehicleDetailFilterEnabled);

    if (filterEnabledTimeoutRef.current) clearTimeout(filterEnabledTimeoutRef.current);
    filterEnabledTimeoutRef.current = setTimeout(() => {
      setVehicleDetailFilterEnabled(draftVehicleDetailFilterEnabled);
    }, VEHICLE_DETAIL_FILTER_DEBOUNCE_MS);
  }, [storageLoaded, draftVehicleDetailFilterEnabled, setVehicleDetailFilterEnabled]);

  const handleGetVehicle = () => {
    const vrm = watch("vrm" as any);
    if (!vrm || fetchLoading || isSearching || disabled) return;
    if (vehicleTimeoutRef.current) clearTimeout(vehicleTimeoutRef.current);
    setFetchLoading(true);
    // Details only — the reg being priced up is usually a customer's car we will never own, so
    // this must not be the lookup that creates a vehicle.
    vrmLookupData.mutate(vrm, {
      onSuccess: (data) => {
        const vehicle = data?.data;
        if (!vehicle) return;
        setFormValue(setValue, "make", (vehicle.combinedMake ?? "").trim());
        setFormValue(setValue, "model", (vehicle.modelRangeDescription ?? "").trim());
        setFormValue(setValue, "bodyStyle", vehicle.bodyStyle);
        setFormValue(setValue, "numberOfDoors", vehicle.numberOfDoors?.toString() ?? "");
        setFormValue(setValue, "fuelType", vehicle.combinedFuelType);
        setFormValue(setValue, "transmissionType", vehicle.combinedTransmission);
        setFormValue(setValue, "engineCode", vehicle.engineModelCode);
        const yearFrom = vehicle.visibilityDate && DateTime.fromISO(vehicle.visibilityDate).year;
        const yearTo = vehicle.terminateDate && DateTime.fromISO(vehicle.terminateDate).year;
        setFormValue(setValue, "yearFrom", yearFrom?.toString() ?? "");
        setFormValue(setValue, "yearTo", yearTo?.toString() ?? "");
        setFormValue(setValue, "engineSize", vehicle.engineCapacity ? (Math.round(vehicle.engineCapacity / 100) * 100 / 1000).toString() : "");
      },
      onSettled: () => {
        vehicleTimeoutRef.current = setTimeout(() => setFetchLoading(false), 500);
      },
    });
  };

  const handleSmartSearch = () => {
    const data = getValues();
    if (!data.vrm || isSearching || fetchLoading) return;
    setIsSearching(true);
    setIsSmartMatch(true);
    onApplySmartSearch({
      smartSearch: data.vrm,
      categoryIds: data.partCategory?.map((category) => category.id),
      ...getAdditionalSmartSearchBody?.(data),
    });
    if (smartMatchTimeoutRef.current) clearTimeout(smartMatchTimeoutRef.current);
    smartMatchTimeoutRef.current = setTimeout(() => setIsSearching(false), 1000);
  };

  useEffect(() => {
    return () => {
      if (vehicleTimeoutRef.current) clearTimeout(vehicleTimeoutRef.current);
      if (smartMatchTimeoutRef.current) clearTimeout(smartMatchTimeoutRef.current);
      if (filterEnabledTimeoutRef.current) clearTimeout(filterEnabledTimeoutRef.current);
    };
  }, []);

  return (
    <>
      <div className="grid grid-cols-1 items-end gap-3 lg:grid-cols-12">
        <div className="lg:col-span-3">
          <label className="text-sm font-medium text-black">{t("Get Vehicle Details")}</label>
          <div className="mt-1 flex items-stretch overflow-hidden rounded-md border-2 border-black shadow-sm focus-within:ring-2 focus-within:ring-primary-500">
            <span aria-hidden="true" className="flex w-6 items-center justify-center bg-blue-700 text-[10px] font-bold uppercase tracking-widest text-yellow-300">UK</span>
            <input
              {...register("vrm" as any)}
              disabled={fetchLoading || isSearching}
              placeholder={t("ENTER VRM / VIN")}
              aria-label={t("VRM or VIN")}
              className="w-full border-0 bg-yellow-300 px-3 py-1 text-center text-xl font-bold uppercase tracking-[0.25em] text-black placeholder:text-sm placeholder:tracking-[0.15em] placeholder:text-black/40 focus:outline-none focus:ring-0 disabled:opacity-60"
              style={{ fontFamily: '"UKNumberPlate", "Charles Wright", "Arial Black", "Helvetica Neue", sans-serif' }}
            />
            <button
              type="button"
              onClick={handleGetVehicle}
              disabled={isLookupDisabled}
              title={fetchLoading ? t("Fetching...") : t("Fetch vehicle details")}
              className={classNames("flex w-9 items-center justify-center bg-black text-yellow-300 transition-colors", isLookupDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-slate-800")}
            >
              {fetchLoading ? <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" /> : <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />}
            </button>
          </div>
        </div>

        <div className="lg:col-span-3">
          <ComboBoxMultipleInput
            label={t("Part Category")}
            register={register}
            registerName="partCategory"
            options={categories}
            setValue={setValue}
            disabled={disabled || categoriesLoading}
            searchField="name"
            classes="w-full"
            placeholder={t("Search categories...")}
            returnObjects
          />
        </div>
        <div className="lg:col-span-1">
          <label className="text-sm font-medium text-wilma-700">{t("MPN")}</label>
          <div className="relative mt-1">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2 text-wilma-500"><HashtagIcon className="h-4 w-4" aria-hidden="true" /></span>
            <input {...register("partNumber" as any)} type="text" placeholder={t("MPN...")} disabled={disabled} className="w-full rounded-md border border-wilma-400 bg-wilma-50 py-2 pl-7 pr-3 text-sm font-medium leading-5 text-wilma-900 placeholder:font-normal placeholder:text-wilma-400 focus:border-wilma-500 focus:outline-none focus:ring-1 focus:ring-wilma-500 disabled:bg-gray-100" />
          </div>
        </div>
        <div className="lg:col-span-2">
          <label className="text-sm font-medium text-wilma-700">{t("Tag Number")}</label>
          <div className="relative mt-1">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2 text-wilma-500"><TagIcon className="h-4 w-4" aria-hidden="true" /></span>
            <input {...register("tagNumber" as any)} type="text" placeholder={t("Tag...")} disabled={disabled} className="w-full rounded-md border border-wilma-400 bg-wilma-50 py-2 pl-7 pr-3 text-sm font-medium leading-5 text-wilma-900 placeholder:font-normal placeholder:text-wilma-400 focus:border-wilma-500 focus:outline-none focus:ring-1 focus:ring-wilma-500 disabled:bg-gray-100" />
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-2 lg:col-span-3 lg:justify-end">
          <ButtonInput label={t("Smart Match")} onClick={handleSmartSearch} classes={classNames("!h-10 !px-3 !py-0", isLookupDisabled ? "" : "!bg-[#00909c] hover:!bg-[#00b3c2]")} disabled={isLookupDisabled} isLoading={isSearching} />
          <PopOutBox position="left" classes="z-30 w-96">
            <p>{t("Smart Match will check for parts that fit your vehicle based on key details like make, model, year, and other specifications.")}</p>
            <p>{t("It finds body parts that match your car's exterior and mechanical parts that fit your engine and transmission.")}</p>
          </PopOutBox>
        </div>
      </div>

      <div className={classNames(isSmartMatch ? "[&_input]:!bg-gray-200" : "", "[&>div]:max-w-[10rem] flex flex-wrap gap-x-2 gap-y-1")}>
        <div><MakeCombobox label={t("Make")} onMakeSelect={(make) => { setFormValue(setValue, "make", make?.make ?? ""); setFormValue(setValue, "model", ""); setFormValue(setValue, "trim", ""); }} currentValue={watchMake} /></div>
        <div><ModelCombobox label={t("Model")} make={watchMake} onModelSelect={(model) => { setFormValue(setValue, "model", model?.range ?? ""); setFormValue(setValue, "trim", ""); }} currentValue={watchModel} /></div>
        <div className="!max-w-[6rem]"><YearCombobox label={t("Year From")} currentValue={watchYearFrom} maxYear={draftVehicleDetailFilterEnabled.yearTo && watchYearTo ? Number(watchYearTo) : undefined} onYearSelect={(year) => setFormValue(setValue, "yearFrom", year ?? "")} placeholder={t("From...")} disabled={disabled} filterEnabled={draftVehicleDetailFilterEnabled.yearFrom} onFilterEnabledChange={(enabled: boolean) => setVehicleDetailFilterEnabledValue("yearFrom", enabled)} /></div>
        <div className="!max-w-[6rem]"><YearCombobox label={t("Year To")} currentValue={watchYearTo} minYear={draftVehicleDetailFilterEnabled.yearFrom && watchYearFrom ? Number(watchYearFrom) : undefined} onYearSelect={(year) => setFormValue(setValue, "yearTo", year ?? "")} placeholder={t("To...")} disabled={disabled} filterEnabled={draftVehicleDetailFilterEnabled.yearTo} onFilterEnabledChange={(enabled: boolean) => setVehicleDetailFilterEnabledValue("yearTo", enabled)} /></div>
        {/* <div><VehicleDetailsInput label="Trim" make={watchMake} model={watchModel} onSelect={(trim) => setFormValue(setValue, "trim", trim ?? "")} currentValue={watch("trim" as any)} filterEnabled={draftVehicleDetailFilterEnabled.trim} onFilterEnabledChange={(enabled: boolean) => setVehicleDetailFilterEnabledValue("trim", enabled)} disabled={disabled} /></div> */}
        <div><FilterStaticCombobox label={t("Body Style")} value={watchBodyStyle} onChange={(value) => setFormValue(setValue, "bodyStyle", value)} options={BODY_STYLE_OPTIONS} disabled={disabled} placeholder={t("Any")} filterEnabled={draftVehicleDetailFilterEnabled.bodyStyle} onFilterEnabledChange={(enabled) => setVehicleDetailFilterEnabledValue("bodyStyle", enabled)} /></div>
        <div><FilterStaticCombobox label={t("Doors")} value={watchNumberOfDoors} onChange={(value) => setFormValue(setValue, "numberOfDoors", value)} options={DOORS_OPTIONS} disabled={disabled} placeholder={t("Any")} filterEnabled={draftVehicleDetailFilterEnabled.numberOfDoors} onFilterEnabledChange={(enabled) => setVehicleDetailFilterEnabledValue("numberOfDoors", enabled)} /></div>
        <div><FilterStaticCombobox label={t("Fuel")} value={watchFuelType} onChange={(value) => setFormValue(setValue, "fuelType", value)} options={FUEL_OPTIONS} disabled={disabled} placeholder={t("Any")} filterEnabled={draftVehicleDetailFilterEnabled.fuelType} onFilterEnabledChange={(enabled) => setVehicleDetailFilterEnabledValue("fuelType", enabled)} /></div>
        <div><FilterStaticCombobox label={t("Engine Size")} value={watchEngineSize} onChange={(value) => setFormValue(setValue, "engineSize", value)} options={ENGINE_SIZE_OPTIONS} disabled={disabled} placeholder={t("Any")} filterEnabled={draftVehicleDetailFilterEnabled.engineSize} onFilterEnabledChange={(enabled) => setVehicleDetailFilterEnabledValue("engineSize", enabled)} /></div>
        <div><VehicleDetailsInput label={t("Engine Code")} onSelect={(value) => setFormValue(setValue, "engineCode", value ?? "")} currentValue={watchEngineCode} filterEnabled={draftVehicleDetailFilterEnabled.engineCode} onFilterEnabledChange={(enabled: boolean) => setVehicleDetailFilterEnabledValue("engineCode", enabled)} disabled={disabled} /></div>
        <div><FilterStaticCombobox label={t("Transmission")} value={watchTransmission} onChange={(value) => setFormValue(setValue, "transmissionType", value)} options={TRANSMISSION_OPTIONS} disabled={disabled} placeholder={t("Any")} filterEnabled={draftVehicleDetailFilterEnabled.transmissionType} onFilterEnabledChange={(enabled) => setVehicleDetailFilterEnabledValue("transmissionType", enabled)} /></div>
      </div>

      {isSmartMatch && (
        <div className="mt-2 rounded-md border border-blue-200 bg-blue-50 p-3">
          <p className="text-sm text-blue-800"><strong>{t("Smart Match Active:")}</strong> {t("Searching for parts that match your vehicle specifications. Vehicle details are locked and cannot be modified while smart match is active.")}</p>
        </div>
      )}
    </>
  );
}
